/**
 * @fileoverview Shared primitives for builder help illustrations (Phase 5, Workstream J3).
 * @module ui-admin/help/illustrations/illustrationPrimitives
 *
 * Responsibilities: - Provide the numbered-callout badge (`CalloutBadge`) drawn on an SVG and
 *                     the numbered legend (`IllustrationLegend`) shown beneath a diagram, plus
 *                     the `IllustrationFigure` wrapper that pairs an SVG with its legend.
 * Dependencies:     - React only.
 * Architecture:     - Presentation-only SVG/HTML helpers. No state, no side effects, no data.
 *                     Shared by every `Illustration*` component so callouts look identical.
 * Notes:            - Colours mirror the Phase 2 canvas Tailwind palette (orange-500 #f97316,
 *                     gray borders) so illustrations read as the real UI.
 */

import React from 'react';

/** Palette constants matching the Phase 2 canvas so illustrations are faithful. */
export const ILLUS = {
  orange: '#f97316',
  orangeDark: '#ea580c',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray700: '#374151',
  gray800: '#1f2937',
  violet: '#7c3aed',
  violet50: '#f5f3ff',
  violet200: '#ddd6fe',
  white: '#ffffff',
} as const;

/**
 * @component CalloutBadge
 * Purpose: Draw a numbered orange circle at (x, y) on an SVG to mark a UI region.
 * Props: n (callout number), x/y (centre in SVG user units), r (radius, default 9).
 */
export function CalloutBadge({ n, x, y, r = 9 }: { n: number; x: number; y: number; r?: number }) {
  return (
    <g aria-hidden="true">
      <circle cx={x} cy={y} r={r} fill={ILLUS.orange} stroke={ILLUS.white} strokeWidth={1.5} />
      <text x={x} y={y + 3.2} textAnchor="middle" fontSize={10} fontWeight={700} fill={ILLUS.white}>
        {n}
      </text>
    </g>
  );
}

/**
 * @component IllustrationLegend
 * Purpose: Render the numbered key beneath a diagram so each callout has an explanation.
 * Props: items (ordered list of callout descriptions; index + 1 is the callout number).
 */
export function IllustrationLegend({ items }: { items: string[] }) {
  return (
    <ol className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2" data-testid="illustration-legend">
      {items.map((text, i) => (
        <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
          <span
            className="mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white"
            aria-hidden="true"
          >
            {i + 1}
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * @component IllustrationFigure
 * Purpose: Wrap an SVG diagram with an accessible title and its numbered legend.
 * Props: title (accessible caption), viewBox, children (SVG content), legend (callout key).
 */
export function IllustrationFigure({
  title,
  viewBox,
  children,
  legend,
}: {
  title: string;
  viewBox: string;
  children: React.ReactNode;
  legend: string[];
}) {
  return (
    <figure className="rounded-lg border border-gray-200 bg-white p-3" data-testid="illustration-figure">
      <svg
        viewBox={viewBox}
        role="img"
        aria-label={title}
        className="w-full"
        style={{ maxHeight: 320 }}
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        <title>{title}</title>
        {children}
      </svg>
      <IllustrationLegend items={legend} />
    </figure>
  );
}
